import React from "react";
import {
  Box,
  Divider,
  Group,
  NumberInput,
  Stack,
  Switch,
  Text,
  Title,
} from "@mantine/core";
import { ActionRewardsDraft } from "./types";

interface ActionRewardsFormProps {
  value: ActionRewardsDraft;
  onChange: (value: ActionRewardsDraft) => void;
  /** Hide the step heading when embedded in a Settings edit modal. */
  showHeading?: boolean;
}

function normalizeNonNegative(raw: number | string): number | string {
  if (typeof raw === "number" && raw < 0) return 0;
  return raw;
}

/**
 * Step 3 — Action Based Rewards (loyalty FE1 §2.3).
 *
 * Three toggle cards, each publishing one earn rule:
 *   Immediate Rebooking → rule_type='rebooking' (always unlimited).
 *   Visits              → rule_type='nth_visit' with interval_count=N (N > 1).
 *   First appointment   → rule_type='nth_visit' with interval_count=1,
 *                         max_awards_per_client=1 (event-based, once per client).
 */
export default function ActionRewardsForm({
  value,
  onChange,
  showHeading = true,
}: ActionRewardsFormProps) {
  return (
    <Stack gap="lg">
      {showHeading && (
        <Stack gap={4}>
          <Title order={2}>Action Based Rewards</Title>
          <Text size="sm" c="dimmed">
            Give points for things like rebooking, visiting often, or a client's
            first appointment. Pick any of the types, and adjust the amounts.
          </Text>
        </Stack>
      )}

      <Box p="lg">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={2}>
              <Title order={4}>Immediate Rebooking (within 24 hours)</Title>
              <Text size="sm" c="dimmed">
                Reward clients for rebooking with you
              </Text>
            </Stack>
            <Switch
              checked={value.rebookingEnabled}
              onChange={(e) =>
                onChange({
                  ...value,
                  rebookingEnabled: e.currentTarget.checked,
                })
              }
              size="md"
              color="var(--brand-teal)"
              aria-label="Enable rebooking reward"
            />
          </Group>
          {value.rebookingEnabled && (
            <NumberInput
              label="Number of points earned"
              value={value.rebookingPoints}
              onChange={(raw) =>
                onChange({
                  ...value,
                  rebookingPoints: normalizeNonNegative(raw),
                })
              }
              min={0}
              clampBehavior="none"
              suffix=" points"
              w={200}
              hideControls
            />
          )}
        </Stack>
      </Box>

      <Divider color="gray.3" />

      <Box p="lg">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={2}>
              <Title order={4}>Visits</Title>
              <Text size="sm" c="dimmed">
                Reward clients for coming back to visit
              </Text>
            </Stack>
            <Switch
              checked={value.visitsEnabled}
              onChange={(e) =>
                onChange({ ...value, visitsEnabled: e.currentTarget.checked })
              }
              size="md"
              color="var(--brand-teal)"
              aria-label="Enable visits reward"
            />
          </Group>
          {value.visitsEnabled && (
            // Each field on its own line (label on top), input width matches
            // the label copy underneath.
            <Stack gap="md">
              <NumberInput
                label="Number of points earned"
                value={value.visitsPoints}
                onChange={(raw) =>
                  onChange({
                    ...value,
                    visitsPoints: normalizeNonNegative(raw),
                  })
                }
                min={0}
                clampBehavior="none"
                suffix=" points"
                w={200}
                hideControls
              />
              <NumberInput
                label="Award after every how many visits?"
                value={value.visitsInterval}
                onChange={(raw) =>
                  onChange({
                    ...value,
                    visitsInterval: normalizeNonNegative(raw),
                  })
                }
                min={1}
                clampBehavior="none"
                w={260}
                hideControls
              />
            </Stack>
          )}
        </Stack>
      </Box>

      <Divider color="gray.3" />

      <Box p="lg">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={2}>
              <Title order={4}>First appointment</Title>
              <Text size="sm" c="dimmed">
                Welcome new clients with points on their first appointment
              </Text>
            </Stack>
            <Switch
              checked={value.firstAppointmentEnabled}
              onChange={(e) =>
                onChange({
                  ...value,
                  firstAppointmentEnabled: e.currentTarget.checked,
                })
              }
              size="md"
              color="var(--brand-teal)"
              aria-label="Enable first appointment reward"
            />
          </Group>
          {value.firstAppointmentEnabled && (
            <NumberInput
              label="Number of points earned"
              aria-label="Points earned for first appointment"
              value={value.firstAppointmentPoints}
              onChange={(raw) =>
                onChange({
                  ...value,
                  firstAppointmentPoints: normalizeNonNegative(raw),
                })
              }
              min={0}
              clampBehavior="none"
              suffix=" points"
              w={200}
              hideControls
            />
          )}
        </Stack>
      </Box>
    </Stack>
  );
}
